import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, mainSiteUrl } from '@/mocks/articles';
import type { ArticleData } from '@/mocks/articles';

interface ArticleLayoutProps {
  slug: string;
}

export default function ArticleLayout({ slug }: ArticleLayoutProps) {
  const navigate = useNavigate();
  const article: ArticleData | undefined = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    if (!article) {
      navigate('/', { replace: true });
    }
  }, [article, navigate]);

  if (!article) return null;

  const renderSection = (section: ArticleData['sections'][0], idx: number) => {
    switch (section.type) {
      case 'heading':
        return (
          <h2 key={idx} className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4 mt-10 first:mt-0">
            {section.title}
          </h2>
        );
      case 'text':
        return (
          <p key={idx} className="text-foreground-600 leading-relaxed mb-4">
            {section.content}
          </p>
        );
      case 'list':
        return (
          <ul key={idx} className="space-y-3 mb-6">
            {section.items?.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-foreground-600 leading-relaxed">
                <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mt-0.5">
                  <i className="ri-check-line text-xs"></i>
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      case 'cards':
        return (
          <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            {section.cards?.map((card, i) => (
              <div key={i} className="p-5 rounded-xl bg-background-100 border border-background-200/60">
                <h3 className="font-heading text-base text-foreground-900 mb-2">{card.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        );
      case 'table':
        return (
          <div key={idx} className="overflow-x-auto my-6">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-background-200">
                  {section.headers?.map((h, i) => (
                    <th key={i} className="text-left py-3 px-4 text-foreground-900 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.rows?.map((row, i) => (
                  <tr key={i} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                    {row.map((cell, j) => (
                      <td key={j} className={`py-3 px-4 ${j === 0 ? 'text-foreground-800 font-medium whitespace-nowrap' : 'text-foreground-500'}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'image':
        return section.imageUrl ? (
          <div key={idx} className="rounded-2xl overflow-hidden my-8 aspect-[21/9] bg-background-100">
            <img
              src={section.imageUrl}
              alt={section.imageAlt || ''}
              className="w-full h-full object-cover"
            />
          </div>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <>
      <Navbar />

      <article className="pt-24 pb-16 md:pt-28 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground-700 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600">{article.title.length > 60 ? article.title.substring(0, 60) + '...' : article.title}</span>
          </nav>

          {/* Article Header */}
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">{article.badge}</span>
              <span className="text-xs text-foreground-400">Updated August 2026</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight mb-6">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-500 leading-relaxed">
              {article.description}
            </p>
          </header>

          {/* Featured Image */}
          <div className="rounded-2xl overflow-hidden mb-12 aspect-[21/9] bg-background-100">
            <img
              src={article.featuredImage}
              alt={article.featuredImageAlt}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Content */}
          <div className="text-foreground-700 leading-relaxed space-y-6">
            {article.sections.map((section, idx) => renderSection(section, idx))}
          </div>

          {/* CTA Section */}
          <div className="mt-14 p-8 md:p-10 rounded-2xl bg-background-100 border border-background-200/60 text-center">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-3">{article.ctaTitle}</h2>
            <p className="text-foreground-500 mb-6 max-w-md mx-auto text-sm md:text-base">
              {article.ctaText}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-cart-2-line"></i>
                Visit Sugargoo Spreadsheet
              </a>
              <Link
                to="/"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full border-2 border-secondary-300 text-secondary-700 font-semibold text-sm hover:bg-secondary-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-4-line"></i>
                Back to Home
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          <nav className="mt-10 pt-8 border-t border-background-200/60">
            <h3 className="font-heading text-lg text-foreground-900 mb-4">More Sugargoo Guides</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {articles
                .filter((a) => a.slug !== article.slug)
                .slice(0, 6)
                .map((a) => (
                  <Link
                    key={a.slug}
                    to={`/article/${a.slug}`}
                    className="group p-4 rounded-xl bg-background-100 border border-background-200/60 hover:border-secondary-200 transition-all duration-200 cursor-pointer"
                  >
                    <span className="text-xs text-primary-600 font-semibold">{a.badge}</span>
                    <h4 className="font-heading text-sm text-foreground-800 mt-1 group-hover:text-foreground-950 transition-colors leading-snug">
                      {a.title.length > 60 ? a.title.substring(0, 60) + '...' : a.title}
                    </h4>
                  </Link>
                ))}
            </div>
            <div className="mt-4 flex items-center gap-4 text-sm">
              <span className="text-foreground-400">Related:</span>
              <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors font-medium">
                Home — Sugargoo Spreadsheet Hub
              </Link>
            </div>
          </nav>
        </div>
      </article>

      {/* Article Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.description,
            "image": article.featuredImage,
            "datePublished": "2026-08-04",
            "dateModified": "2026-08-05",
            "author": {
              "@type": "Organization",
              "name": "sugargoosheet.xyz",
              "url": import.meta.env.VITE_SITE_URL
            },
            "publisher": {
              "@type": "Organization",
              "name": "sugargoosheet.xyz",
              "url": import.meta.env.VITE_SITE_URL,
              "logo": {
                "@type": "ImageObject",
                "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": import.meta.env.VITE_SITE_URL + "/article/" + article.slug
            }
          })
        }}
      />

      <Footer />
    </>
  );
}